import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppShell } from '../contexts/AppShellContext';
import { DEMO_RDQ_ROWS, DEMO_JOBS } from '../data/demoProxyData';

const RDQ_JOB_ID = '3040869'; // Water Corp

export function ProxyRDQPage() {
  const { setTopbar } = useAppShell();
  const [searchParams] = useSearchParams();
  const [ran, setRan] = useState(false);

  useEffect(() => {
    setTopbar('RDQ Calculator', 'Record-date quantity calculator — ICT-6 fallback when Alteryx is unavailable');
  }, [setTopbar]);

  // Steps 4+ show results — auto-run calculator so user sees the output immediately
  useEffect(() => {
    const idea = parseInt(searchParams.get('idea') ?? '0', 10);
    const step = parseInt(searchParams.get('step') ?? '0', 10);
    if (idea === 5 && step >= 4) setRan(true);
    if (idea === 5 && step <= 3) setRan(false);
  }, [searchParams]);

  const job = DEMO_JOBS[RDQ_JOB_ID];
  const kept    = DEMO_RDQ_ROWS.filter(r => r.keep);
  const excluded = DEMO_RDQ_ROWS.filter(r => !r.keep && r.reason && !r.reason.includes('Green'));
  const purged  = DEMO_RDQ_ROWS.filter(r => r.reason?.includes('Green'));
  const total   = kept.reduce((sum, r) => sum + parseInt(r.sets, 10), 0);
  const micSets = job?.rdqSets ?? 2100;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 overflow-auto px-6 py-6">
        {/* Job selector */}
        <div className="flex items-center gap-4 mb-5">
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-trim bg-white">
            <span className="text-xs text-muted">Job</span>
            <span className="text-xs font-semibold text-ink">{job?.issuer} — {RDQ_JOB_ID}</span>
            <span className="text-xs text-muted">|</span>
            <span className="text-xs text-muted">MIC quantity:</span>
            <span className="text-xs font-semibold text-ink">{micSets.toLocaleString()} sets</span>
          </div>
          <button
            onClick={() => setRan(true)}
            className="px-4 py-2.5 rounded-lg text-xs font-semibold text-white"
            style={{ background: ran ? '#0f6e56' : 'linear-gradient(135deg,#14b8a6,#0891b2)' }}
          >
            {ran ? 'Recalculate' : 'Run calculator'}
          </button>
        </div>

        <div className="flex gap-6 items-start">
          {/* Extract table */}
          <div data-tour="rdq-extract" className="flex-1 rounded-2xl border border-trim overflow-hidden">
            <div className="px-4 py-3 border-b border-trim bg-[#F8FAFC] flex items-center justify-between">
              <p className="text-xs font-semibold text-ink">Report-server extract</p>
              <p className="text-xs text-muted">{DEMO_RDQ_ROWS.length} rows</p>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-trim bg-[#F8FAFC]">
                  <th className="text-left px-4 py-2.5 font-semibold text-muted">Holder</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-muted">Class</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-muted">Sets</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-muted">Result</th>
                </tr>
              </thead>
              <tbody>
                {DEMO_RDQ_ROWS.map((row, i) => {
                  const isGreen = row.reason?.includes('Green');
                  const isExcluded = !row.keep && !isGreen;
                  return (
                    <tr
                      key={i}
                      data-tour={isGreen ? 'rdq-green' : isExcluded && row.kind === 'NOBO' ? 'rdq-nobo' : undefined}
                      className="border-b border-trim last:border-0"
                      style={{ opacity: ran && !row.keep ? 0.55 : 1 }}
                    >
                      <td className="px-4 py-2.5 text-ink" style={{ textDecoration: ran && !row.keep ? 'line-through' : 'none' }}>{row.holder}</td>
                      <td className="px-4 py-2.5">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                          style={{
                            background: isGreen ? '#F0FDF4' : isExcluded ? '#FEF2F2' : '#EFF6FF',
                            color:      isGreen ? '#166534' : isExcluded ? '#991b1b' : '#1e40af',
                          }}>
                          {row.kind}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-muted">{row.sets}</td>
                      <td className="px-4 py-2.5">
                        {ran ? (
                          row.keep
                            ? <span className="text-emerald-600 font-semibold">Keep</span>
                            : <span className="text-muted">{row.reason}</span>
                        ) : <span className="text-muted">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Result panel */}
          <div className="w-80 flex-shrink-0 space-y-4">
            <div data-tour="rdq-result" className="rounded-2xl border border-trim overflow-hidden">
              <div className="px-4 py-3 border-b border-trim bg-[#F8FAFC]">
                <p className="text-xs font-semibold text-ink">Result</p>
              </div>
              {ran ? (
                <div className="px-4 py-4 space-y-3">
                  <div className="text-3xl font-bold text-ink">{total.toLocaleString()}</div>
                  <p className="text-xs text-muted">sets after ICT-6 exclusions</p>
                  <div className="border-t border-trim pt-3 space-y-1.5 text-xs">
                    <p className="text-muted">Trace:</p>
                    <p className="text-ink">{DEMO_RDQ_ROWS.length} rows in extract</p>
                    <p className="text-muted">− {purged.length} rows purged (Green status)</p>
                    {excluded.map(r => (
                      <p key={r.holder} className="text-muted">− {r.sets} sets ({r.kind})</p>
                    ))}
                    <p className="font-semibold text-ink border-t border-trim pt-1.5">= {total.toLocaleString()} sets</p>
                  </div>
                  <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
                    <p className="text-xs text-amber-700">MIC shows {micSets.toLocaleString()} — delta {(micSets - total).toLocaleString()} explained by exclusions above.</p>
                  </div>
                </div>
              ) : (
                <div className="px-4 py-6 text-center text-xs text-muted">Run the calculator to see results.</div>
              )}
            </div>

            {/* Shortage check */}
            <div data-tour="rdq-shortage" className="rounded-2xl border border-trim overflow-hidden">
              <div className="px-4 py-3 border-b border-trim bg-[#F8FAFC]">
                <p className="text-xs font-semibold text-ink">Shortage check</p>
              </div>
              <div className="px-4 py-3 text-xs text-muted leading-relaxed">
                Cormorant Energy Partners (Job 4010016): ordered 7, received 4, deficit 3.
                <br /><br />
                This shortage is tracked separately under ICT-13. The 3 missing sets are not subtracted from the Water Corp RDQ total ({ran ? total.toLocaleString() : '—'} sets).
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
